import { Injectable, OnDestroy } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { CookieService } from "ngx-cookie-service";
import { Request } from "@edp-utils/request.utils";
import { ApiService } from "./api.service";
import { Observable, Subject } from "rxjs";
import { Language } from "@edp-models/language.model";
import { TranslateService } from "@ngx-translate/core";
import { environment } from "@edp-environments/environment";
import { LanguageTypeEnum, LanguageEnum, LocaleEnum } from "@edp-enums/language.enum";
import { OperationHistoryService } from "./operation-history.service";
import { select, Store } from "@ngrx/store";
import { IAppState } from "@edp-store/state/app.state";
import { takeUntil } from "rxjs/operators";
import { getContract } from "@edp-store/selectors/contract.selectors";
import { ShowLoader } from "@edp-store/actions/layout.actions";
import { RegionService } from "./region.service";
import { EnvService } from "./env.service";
import { getRegionByTLD } from "@edp-utils/region.utils";
import { RegionEnum } from "@edp-enums/region.enum";

const keyPrefixStorage = "NG_TRANSLATE";
const keyCookie = "user_language";
const keyStorageLanguage = "user_language";
const defaultLanguageRegions = {
    PT: "pt_PT",
    ES: "es_ES",
};

// date to force request to load new labels on deploy
const DEPLOY_DATE = "2025-01-20";
const KEY_STORAGE_LABEL_DATE = "deploy_labels_date";

@Injectable()
export class LanguageService implements OnDestroy {
    public defaultLanguage: LanguageEnum = LanguageEnum.pt_PT;
    public locale: LocaleEnum = LocaleEnum.pt;
    private region: string;
    private contractId: string;
    private unsubscribe = new Subject();

    constructor(
        private http: HttpClient,
        private cookieService: CookieService,
        private apiService: ApiService,
        public store: Store<IAppState>,
        public readonly translate: TranslateService,
        public readonly regionService: RegionService,
        public readonly operationHistoryService: OperationHistoryService,
        public readonly envService: EnvService
    ) {
        this.region = regionService.getRegion();

        this.defaultLanguage = this.getStorageLanguage() || defaultLanguageRegions[this.region] || defaultLanguageRegions.PT;

        this.store.pipe(takeUntil(this.unsubscribe), select(getContract)).subscribe(contract => {
            if (!!contract) {
                this.contractId = contract.ContractId;
            }
        });
    }

    private getStorageTranslate(languageKey: string): object {
        return JSON.parse(localStorage.getItem(`${keyPrefixStorage}${languageKey}`));
    }

    private setTranslateLanguage(languageKey: string, languages: any): void {
        localStorage.setItem(`${keyPrefixStorage}${languageKey}`, JSON.stringify(languages));
    }

    private setStorageLanguage(language: string): void {
        const languageEnum = LanguageEnum[language];
        localStorage.setItem(keyStorageLanguage, languageEnum);
    }

    private getStorageLanguage(): LanguageEnum {
        const language = getRegionByTLD() === RegionEnum.ES ? LanguageEnum.es_ES : LanguageEnum.pt_PT;

        return LanguageEnum[localStorage.getItem(keyStorageLanguage)] || language;
    }

    private forceLabelsByDeployDate(): boolean {
        const storedDeployDate = localStorage.getItem(KEY_STORAGE_LABEL_DATE);

        if (!storedDeployDate || new Date(storedDeployDate) < new Date(DEPLOY_DATE)) {
            localStorage.setItem(KEY_STORAGE_LABEL_DATE, DEPLOY_DATE);
            return true;
        }
        return false;
    }

    public initLanguageTranslate() {
        const language = this.getUserLanguage(true);
        /* this language will be used as a fallback when a translation isn't found in the current language */
        this.translate.setDefaultLang(language);
        this.translate.setTranslation(language, this.getStorageTranslate(language), true);

        /* the lang to use, if the lang isn't available, it will use the current loader to get them */
        this.translate.use(language);
        const convertLanguageToLocale = LanguageTypeEnum[this.getStorageLanguage()];
        this.locale = LocaleEnum[convertLanguageToLocale];
    }

    private requestGetResource(isMobile: boolean): Observable<any> {
        const defaultLanguageParm = defaultLanguageRegions[this.region] || defaultLanguageRegions.PT;
        let resourceUrl = `/client/resources/?region=${this.region}&language=${defaultLanguageParm}`;

        if (isMobile) {
            resourceUrl = `${resourceUrl}&mobile=${isMobile}`;
        }
        return this.http.get(resourceUrl);
    }

    public getLanguageKeys(): Promise<any> {
        return new Promise(resolve => {
            const userLanguage = this.getUserLanguage(true);
            const currentLanguageKeys = this.getStorageTranslate(userLanguage);
            const hasMobileCookie = this.cookieService.get("IsMobile") === "1" ? true : false;
            const isMobile: boolean = Request.getParameter("Mobile") === "1" || hasMobileCookie;
            const forceLabels = this.forceLabelsByDeployDate();

            if (forceLabels || isMobile || !currentLanguageKeys) {
                return this.requestGetResource(isMobile).subscribe((language: any) => {
                    this.setTranslateLanguage(userLanguage, language);
                    this.setStorageLanguage(userLanguage);
                    resolve(language);
                });
            }
            resolve(currentLanguageKeys);
        });
    }

    private setCookieLanguage(value: string): void {
        this.cookieService.delete(keyCookie);
        const now = new Date();
        now.setTime(now.getTime() + 1 * 3600 * 1000 * 24);
        environment.url[this.region].includes("localhost")
            ? this.cookieService.set(keyCookie, value, now)
            : this.cookieService.set(keyCookie, value, now, "/", this.envService.getCookieDomain(), true);
    }

    public setUserLanguage(value: string, reload: boolean = true, sendOperation = true): void {
        if (reload && value === this.getUserLanguage()) {
            return;
        }
        if (!sendOperation) {
            this.setCookieLanguage(value);
            return;
        }
        this.store.dispatch(new ShowLoader(true));
        this.sendOperationHistoryObservable("Click_", value).subscribe(() => {
            this.store.dispatch(new ShowLoader(false));
            this.setCookieLanguage(value);
            if (reload) {
                location.reload();
            }
        });
    }

    public getUserLanguage(onLoadApplication: boolean = false): string {
        const userLanguage = this.cookieService.get(keyCookie);
        if (!userLanguage) {
            const sendOperation = !onLoadApplication;
            this.setUserLanguage(this.defaultLanguage, false, sendOperation);
            return this.defaultLanguage;
        }
        return userLanguage;
    }

    public getLanguages(): Observable<Language[] | Error> {
        return this.apiService.callServicePostToCollection("language.getAll", {});
    }

    public getLocale(): LocaleEnum {
        return this.locale;
    }

    public sendOperationHistoryService(key: string, value: string | null = null) {
        this.operationHistoryService.registerWithContract(this.contractId, `${key}${value}`, value, null, null, false);
    }

    public sendOperationHistoryObservable(key: string, value: string | null = null): Observable<any> {
        return this.operationHistoryService.registerWithContractObservable(this.contractId, `${key}${value}`, value, null, null, false);
    }

    public ngOnDestroy(): void {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}
